import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Calendar,
  Check,
  Clock,
  Copy,
  Download,
  ExternalLink,
  Mail,
  MessageSquare,
  Phone,
  RefreshCw,
  Search,
  Sparkles,
  UserCheck,
  X,
} from "lucide-react";
import { demoRequestsService, DemoRequest } from "src/services/demoRequestsService";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { useToast } from "src/hooks/useToast";
import { ROUTES } from "src/routes/RouteConfig";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/Dialog";
import { tutorsService, Tutor } from "src/services/tutorsService";

// Semantic Status Badge styling following rfLearn DESIGN.md
export const getStatusBadgeStyle = (status: string) => {
  switch (status) {
    case "new":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "contacted":
      return "bg-amber-50 text-amber-800 border-amber-200";
    case "qualified":
      return "bg-indigo-50 text-indigo-700 border-indigo-200";
    case "scheduled_demo":
    case "scheduled":
      return "bg-purple-50 text-purple-700 border-purple-200";
    case "converted":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "closed_lost":
    case "cancelled":
      return "bg-rose-50 text-rose-700 border-rose-200";
    default:
      return "bg-slate-100 text-slate-700 border-slate-200";
  }
};

export const getStatusLabel = (status: string) => {
  switch (status) {
    case "new":
      return "New Inquiry";
    case "contacted":
      return "Contacted";
    case "qualified":
      return "Qualified";
    case "scheduled_demo":
    case "scheduled":
      return "Demo Scheduled";
    case "converted":
      return "Converted";
    case "closed_lost":
      return "Archived / Lost";
    default:
      return status.replace(/_/g, " ");
  }
};

const formatRelativeTime = (isoString: string) => {
  try {
    const diffMs = Date.now() - new Date(isoString).getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;
    return new Date(isoString).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return isoString;
  }
};

const DemoBookingsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { setTitle, setDescription, reset } = usePageHeaderStore();

  // State
  const [activeTab, setActiveTab] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedGrade, setSelectedGrade] = useState<string>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [adminNoteInput, setAdminNoteInput] = useState<string>("");
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Demo Booking Schedule Dialog State
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("16:00");
  const [scheduleTutorId, setScheduleTutorId] = useState("");

  // Setup page header
  useEffect(() => {
    setTitle("Demo Bookings & Triage");
    setDescription(
      "Triage prospective student inquiries, qualify curriculum goals, and dispatch trial sessions."
    );
    return () => reset();
  }, [setTitle, setDescription, reset]);

  // Query: Demo Requests
  const {
    data: allRequests = [],
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["admin-demo-bookings"],
    queryFn: () => demoRequestsService.fetchList({ limit: 150 }),
    staleTime: 60 * 1000,
  });

  // Query: Available Tutors for demo assignment
  const { data: tutorsData } = useQuery({
    queryKey: ["all-tutors-for-demo"],
    queryFn: () => tutorsService.fetchAllTutorData(),
    staleTime: 5 * 60 * 1000,
  });

  const availableTutors = useMemo(() => {
    return tutorsData?.tutors || [];
  }, [tutorsData]);

  // Status Counts
  const counts = useMemo(() => {
    const map: Record<string, number> = {
      all: allRequests.length,
      new: 0,
      contacted: 0,
      qualified: 0,
      scheduled_demo: 0,
      converted: 0,
      closed_lost: 0,
    };
    allRequests.forEach((req) => {
      const s = req.status === "scheduled" ? "scheduled_demo" : req.status;
      if (map[s] !== undefined) {
        map[s]++;
      }
    });
    return map;
  }, [allRequests]);

  // Grade filter options
  const gradeOptions = useMemo(() => {
    const set = new Set<string>();
    allRequests.forEach((r) => {
      if (r.grade) set.add(r.grade);
    });
    return Array.from(set).sort();
  }, [allRequests]);

  // Filtered requests list
  const filteredRequests = useMemo(() => {
    return allRequests.filter((req) => {
      // Tab filter
      if (activeTab !== "all") {
        const normalizedStatus = req.status === "scheduled" ? "scheduled_demo" : req.status;
        if (normalizedStatus !== activeTab) return false;
      }

      // Grade filter
      if (selectedGrade !== "all" && req.grade !== selectedGrade) {
        return false;
      }

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const searchable = [
          req.student_name,
          req.parent_name,
          req.email,
          req.parent_phone,
          req.grade,
          req.source,
          ...(req.primary_goals || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!searchable.includes(q)) return false;
      }

      return true;
    });
  }, [allRequests, activeTab, selectedGrade, searchQuery]);

  // Ensure an item is selected by default when list loads
  useEffect(() => {
    if (filteredRequests.length > 0) {
      if (!selectedId || !filteredRequests.some((r) => r.id === selectedId)) {
        setSelectedId(filteredRequests[0].id);
      }
    } else {
      setSelectedId(null);
    }
  }, [filteredRequests, selectedId]);

  // Currently selected lead
  const selectedLead = useMemo(() => {
    return allRequests.find((r) => r.id === selectedId) || null;
  }, [allRequests, selectedId]);

  // Sync internal notes input when selection changes
  useEffect(() => {
    if (selectedLead) {
      setAdminNoteInput(selectedLead.notes || "");
    }
  }, [selectedLead]);

  // Mutation: Update status
  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      demoRequestsService.updateStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin-demo-bookings"] });
      toast({
        title: "Status Updated",
        description: `Lead status changed to ${getStatusLabel(variables.status)}.`,
        type: "success",
      });
    },
    onError: (err: any) => {
      toast({
        title: "Update Failed",
        description: err.message || "Could not update status",
        type: "error",
      });
    },
  });

  // Mutation: Update notes
  const notesMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      demoRequestsService.updateNotes(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-demo-bookings"] });
      toast({
        title: "Notes Saved",
        description: "Internal diagnostic notes have been updated.",
        type: "success",
      });
    },
    onError: (err: any) => {
      toast({
        title: "Save Failed",
        description: err.message || "Could not save notes",
        type: "error",
      });
    },
  });

  // Mutation: Schedule Demo
  const scheduleDemoMutation = useMutation({
    mutationFn: async ({
      id,
      tutorId,
    }: {
      id: string;
      tutorId: string;
      date: string;
      time: string;
    }) => {
      // 1. Assign tutor
      await demoRequestsService.assignTutor(id, tutorId || null);
      // 2. Mark status as scheduled_demo
      await demoRequestsService.updateStatus(id, "scheduled_demo");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-demo-bookings"] });
      setIsScheduleModalOpen(false);
      toast({
        title: "Demo Session Scheduled",
        description: "Trial demo slot confirmed and lead marked as scheduled.",
        type: "success",
      });
    },
    onError: (err: any) => {
      toast({
        title: "Scheduling Failed",
        description: err.message || "Failed to schedule demo session",
        type: "error",
      });
    },
  });

  // Clipboard copy helper
  const handleCopy = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    toast({
      title: "Copied to Clipboard",
      description: `${fieldName} copied.`,
      type: "info",
      duration: 2000,
    });
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Export CSV
  const handleExportCSV = () => {
    if (filteredRequests.length === 0) {
      toast({
        title: "Export Empty",
        description: "No demo requests match current filters to export.",
        type: "warning",
      });
      return;
    }

    const headers = [
      "Inquiry ID",
      "Created At",
      "Student Name",
      "Parent Name",
      "Email",
      "Phone",
      "Grade",
      "Primary Goals",
      "Status",
      "Source",
      "Notes",
    ];

    const rows = filteredRequests.map((r) => [
      `"${r.id}"`,
      `"${r.created_at}"`,
      `"${r.student_name.replace(/"/g, '""')}"`,
      `"${r.parent_name.replace(/"/g, '""')}"`,
      `"${r.email}"`,
      `"${[r.phone_isd_code, r.parent_phone].filter(Boolean).join(" ")}"`,
      `"${(r.grade || "").replace(/"/g, '""')}"`,
      `"${(r.primary_goals?.join("; ") || "").replace(/"/g, '""')}"`,
      `"${r.status}"`,
      `"${r.source || ""}"`,
      `"${(r.notes || "").replace(/"/g, '""')}"`,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `rflearn_demo_bookings_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "CSV Exported",
      description: `Downloaded ${filteredRequests.length} demo inquiries.`,
      type: "success",
    });
  };

  // Helper for international phone
  const cleanPhone = selectedLead?.parent_phone?.replace(/[^\d+]/g, "") || "";
  const phoneISD = selectedLead?.phone_isd_code?.replace(/[^\d+]/g, "") || "+91";
  const fullDialNumber = cleanPhone.startsWith("+")
    ? cleanPhone
    : `${phoneISD}${cleanPhone.replace(/^0+/, "")}`;

  return (
    <div className="space-y-5 pb-12 font-sans">
      {/* Telemetry & KPI Metric Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-100">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">
                Demo Inquiries & Triage Console
              </h1>
              <span className="bg-purple-50 text-purple-700 text-xs font-bold px-2 py-0.5 rounded-full border border-purple-200">
                {counts.new} Action Required
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Review parent diagnostic requests, qualify curricula, and dispatch accredited tutors for trial sessions.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleExportCSV}
              className="h-8 px-3 text-xs font-semibold bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded-lg flex items-center gap-1.5 shadow-2xs transition"
            >
              <Download className="w-3.5 h-3.5 text-slate-500" />
              Export CSV
            </button>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="h-8 px-3 text-xs font-semibold bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 rounded-lg flex items-center gap-1.5 shadow-2xs transition disabled:opacity-50"
            >
              <RefreshCw
                className={`w-3.5 h-3.5 ${isFetching ? "animate-spin text-purple-600" : ""}`}
              />
              Refresh
            </button>
            <button
              onClick={() => navigate(ROUTES.ADMIN_ENROLLMENTS_CREATE.path)}
              className="h-8 px-3 text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-1.5 shadow-2xs transition"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Manual Enrollment
            </button>
          </div>
        </div>

        {/* Operational Metrics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4">
          <div className="p-3 bg-slate-50/70 border border-slate-200/80 rounded-lg">
            <span className="text-xs font-semibold text-slate-500 block">
              Total Inquiries
            </span>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-lg font-bold text-slate-900 font-mono">
                {counts.all}
              </span>
              <span className="text-xs text-slate-500 font-medium">all-time records</span>
            </div>
          </div>

          <div className="p-3 bg-blue-50/40 border border-blue-200/60 rounded-lg">
            <span className="text-xs font-semibold text-blue-900 block">
              New Inbound Leads
            </span>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-lg font-bold text-blue-700 font-mono">
                {counts.new}
              </span>
              <span className="text-xs font-bold text-blue-600">Pending initial call</span>
            </div>
          </div>

          <div className="p-3 bg-purple-50/40 border border-purple-200/60 rounded-lg">
            <span className="text-xs font-semibold text-purple-900 block">
              Demos Confirmed
            </span>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-lg font-bold text-purple-700 font-mono">
                {counts.scheduled_demo}
              </span>
              <span className="text-xs font-medium text-purple-600">Slots locked</span>
            </div>
          </div>

          <div className="p-3 bg-emerald-50/40 border border-emerald-200/60 rounded-lg">
            <span className="text-xs font-semibold text-emerald-900 block">
              Converted to Plan
            </span>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-lg font-bold text-emerald-700 font-mono">
                {counts.converted}
              </span>
              <span className="text-xs font-bold text-emerald-700">
                {counts.all > 0
                  ? `${Math.round((counts.converted / counts.all) * 100)}% Conversion`
                  : "0%"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Split-Screen Workbench (Option A) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
        {/* Left Pane: Triage Master List (7 cols) */}
        <div className="lg:col-span-7 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
          {/* Flush Tab Navigation matching DESIGN.md */}
          <div className="border-b border-slate-200 bg-[#faf9fe] px-4 pt-2">
            <div className="flex items-center gap-1 -mb-px overflow-x-auto">
              {[
                { id: "all", label: "All", count: counts.all },
                { id: "new", label: "New", count: counts.new },
                { id: "contacted", label: "Contacted", count: counts.contacted },
                { id: "qualified", label: "Qualified", count: counts.qualified },
                { id: "scheduled_demo", label: "Scheduled", count: counts.scheduled_demo },
                { id: "converted", label: "Converted", count: counts.converted },
              ].map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`-mb-px border-b-2 py-3 px-3 text-xs transition whitespace-nowrap flex items-center gap-1.5 focus:outline-none ${
                      isActive
                        ? "border-purple-600 bg-transparent text-purple-700 font-bold"
                        : "border-transparent text-slate-600 hover:text-slate-900 font-medium"
                    }`}
                  >
                    {tab.label}
                    <span
                      className={`text-xs font-mono font-bold px-1.5 py-0.2 rounded-full ${
                        isActive
                          ? "bg-purple-100 text-purple-800"
                          : "bg-slate-200 text-slate-700"
                      }`}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick Filters */}
          <div className="p-3 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by student, parent, email, phone..."
                className="w-full pl-8 pr-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-purple-600 shadow-2xs"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <select
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value)}
              className="h-8 px-2.5 py-1 text-xs border border-slate-300 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-600 shadow-2xs font-medium"
            >
              <option value="all">All Grades</option>
              {gradeOptions.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </div>

          {/* Lead List Items */}
          <div className="divide-y divide-slate-100 max-h-[640px] overflow-y-auto">
            {isLoading ? (
              <div className="p-12 text-center text-slate-500 text-xs">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto text-purple-600 mb-2" />
                Loading demo bookings...
              </div>
            ) : isError ? (
              <div className="p-6 text-center text-rose-600 text-xs flex flex-col items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                <p>{(error as any)?.message || "Failed to load demo requests"}</p>
                <button
                  onClick={() => refetch()}
                  className="text-purple-700 underline font-semibold"
                >
                  Try Again
                </button>
              </div>
            ) : filteredRequests.length === 0 ? (
              <div className="p-12 text-center text-slate-500 text-xs space-y-1">
                <p className="font-semibold text-slate-700">No matching demo inquiries</p>
                <p>Try clearing your search query or selecting a different status filter.</p>
              </div>
            ) : (
              filteredRequests.map((req) => {
                const isSelected = req.id === selectedId;
                const statusBadge = getStatusBadgeStyle(req.status);
                const statusLabel = getStatusLabel(req.status);
                const phoneStr = [req.phone_isd_code, req.parent_phone]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <div
                    key={req.id}
                    onClick={() => setSelectedId(req.id)}
                    className={`p-4 cursor-pointer transition text-xs border-b border-slate-100 flex items-start justify-between gap-3 ${
                      isSelected
                        ? "bg-purple-50/60 ring-1 ring-purple-600/30"
                        : "bg-white hover:bg-slate-50/80"
                    }`}
                  >
                    <div className="space-y-1.5 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-900 text-xs">
                          {req.student_name}
                        </span>
                        <span className="text-slate-400">•</span>
                        <span className="text-slate-600 font-medium truncate">
                          Parent: {req.parent_name}
                        </span>
                        <span className="text-slate-400 font-mono text-xs">
                          {formatRelativeTime(req.created_at)}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5 flex-wrap">
                        {req.grade && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 border border-slate-200 text-slate-700">
                            {req.grade}
                          </span>
                        )}
                        {req.primary_goals && req.primary_goals.length > 0 && (
                          <span className="text-xs text-purple-700 font-semibold bg-purple-50 px-2 py-0.5 rounded border border-purple-200 truncate max-w-[240px]">
                            {req.primary_goals.join(", ")}
                          </span>
                        )}
                        {phoneStr && (
                          <span className="text-xs text-slate-500 font-mono">
                            {phoneStr}
                          </span>
                        )}
                      </div>

                      {req.notes && (
                        <p className="text-xs text-slate-600 italic line-clamp-1">
                          "{req.notes}"
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <span className="text-xs font-mono text-slate-400">
                        #{req.id.slice(0, 6)}
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${statusBadge}`}
                      >
                        {statusLabel}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Pane: Diagnostic Dossier & Action Cockpit (5 cols) */}
        <div className="lg:col-span-5 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs sticky top-4">
          {selectedLead ? (
            <div>
              {/* Dossier Header */}
              <div className="bg-[#faf9fe] border-b border-slate-200 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono font-semibold text-purple-700 bg-purple-100 px-2 py-0.5 rounded">
                        #{selectedLead.id.slice(0, 8)}
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${getStatusBadgeStyle(
                          selectedLead.status
                        )}`}
                      >
                        {getStatusLabel(selectedLead.status)}
                      </span>
                    </div>
                    <h2 className="text-lg font-bold text-slate-900 mt-1.5">
                      {selectedLead.student_name}
                    </h2>
                    <p className="text-xs text-slate-500">
                      Submitted on{" "}
                      {new Date(selectedLead.created_at).toLocaleDateString(undefined, {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>

                  {/* Action Button Trio */}
                  <div className="flex items-center gap-1.5">
                    <button
                      title="Copy dossier details"
                      onClick={() =>
                        handleCopy(
                          `rfLearn Inquiry: ${selectedLead.student_name} (Grade: ${selectedLead.grade || "N/A"})\nParent: ${selectedLead.parent_name} (${selectedLead.email})\nPhone: ${[selectedLead.phone_isd_code, selectedLead.parent_phone].filter(Boolean).join(" ")}\nGoals: ${selectedLead.primary_goals?.join(", ") || "General"}`,
                          "Lead Dossier"
                        )
                      }
                      className="h-8 px-2.5 text-xs font-semibold bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded-lg flex items-center gap-1 shadow-2xs transition"
                    >
                      {copiedField === "Lead Dossier" ? (
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                      ) : (
                        <Copy className="w-3.5 h-3.5 text-slate-500" />
                      )}
                    </button>

                    <button
                      title="Mark as Archived / Lost"
                      onClick={() =>
                        statusMutation.mutate({
                          id: selectedLead.id,
                          status: "closed_lost",
                        })
                      }
                      className="h-8 px-2.5 text-xs font-semibold bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-lg flex items-center gap-1 shadow-2xs transition"
                    >
                      Archive
                    </button>
                  </div>
                </div>

                {/* Direct Outreach Action Strip */}
                <div className="grid grid-cols-2 gap-2 mt-3.5 pt-3 border-t border-purple-100">
                  <a
                    href={fullDialNumber ? `tel:${fullDialNumber}` : "#"}
                    className={`h-8 flex items-center justify-center gap-1.5 bg-white hover:bg-slate-50 border border-slate-300 rounded-lg text-xs font-bold text-slate-800 shadow-2xs transition ${
                      !fullDialNumber ? "opacity-50 pointer-events-none" : ""
                    }`}
                  >
                    <Phone className="w-3.5 h-3.5 text-emerald-600" />
                    Call Parent
                  </a>

                  <a
                    href={
                      cleanPhone
                        ? `https://wa.me/${cleanPhone.replace("+", "")}?text=${encodeURIComponent(
                            `Hello ${selectedLead.parent_name}, this is from rfLearn regarding your demo request for ${selectedLead.student_name}.`
                          )}`
                        : "#"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`h-8 flex items-center justify-center gap-1.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg text-xs font-bold text-emerald-800 shadow-2xs transition ${
                      !cleanPhone ? "opacity-50 pointer-events-none" : ""
                    }`}
                  >
                    <MessageSquare className="w-3.5 h-3.5 text-emerald-600" />
                    WhatsApp
                  </a>
                </div>
              </div>

              {/* Dossier Body */}
              <div className="p-4 space-y-4 max-h-[520px] overflow-y-auto text-xs">
                {/* Status Transition Control */}
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Lifecycle Status
                  </label>
                  <select
                    value={selectedLead.status}
                    onChange={(e) =>
                      statusMutation.mutate({
                        id: selectedLead.id,
                        status: e.target.value,
                      })
                    }
                    className="w-full h-8 px-2.5 text-xs font-semibold rounded-lg border border-slate-300 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-600 shadow-2xs"
                  >
                    <option value="new">1. New Inquiry</option>
                    <option value="contacted">2. Contacted (Outreach In Progress)</option>
                    <option value="qualified">3. Qualified (Requirements Clear)</option>
                    <option value="scheduled_demo">4. Demo Scheduled</option>
                    <option value="converted">5. Converted (Enrolled)</option>
                    <option value="closed_lost">6. Closed / Lost</option>
                  </select>
                </div>

                {/* Guardian & Student Metadata Grid */}
                <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-slate-500 block">Parent / Guardian:</span>
                      <span className="font-bold text-slate-800">
                        {selectedLead.parent_name}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Phone Number:</span>
                      <span className="font-bold text-slate-800 font-mono">
                        {[selectedLead.phone_isd_code, selectedLead.parent_phone]
                          .filter(Boolean)
                          .join(" ") || "—"}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-200/60">
                    <div>
                      <span className="text-slate-500 block">Email Address:</span>
                      <span className="font-bold text-slate-800 break-all">
                        {selectedLead.email}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Grade / Board:</span>
                      <span className="font-bold text-purple-700">
                        {selectedLead.grade || "Not specified"}
                      </span>
                    </div>
                  </div>

                  {selectedLead.source && (
                    <div className="pt-1 border-t border-slate-200/60 flex items-center justify-between text-slate-500">
                      <span>Source Channel:</span>
                      <span className="font-mono text-slate-700">
                        {selectedLead.source}
                      </span>
                    </div>
                  )}
                </div>

                {/* Curriculum Goals & Objectives */}
                <div>
                  <span className="text-xs font-bold text-slate-700 block mb-1.5">
                    Target Curriculum Objectives
                  </span>
                  {selectedLead.primary_goals && selectedLead.primary_goals.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {selectedLead.primary_goals.map((goal, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-purple-50 text-purple-700 rounded-md font-semibold border border-purple-200"
                        >
                          {goal}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 italic">No specific goals specified.</p>
                  )}
                </div>

                {/* Internal Triage Notes */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-bold text-slate-700">
                      Internal Dispatch Notes
                    </label>
                    {selectedLead.notes !== adminNoteInput && (
                      <span className="text-xs text-amber-600 font-medium">Unsaved</span>
                    )}
                  </div>
                  <textarea
                    rows={3}
                    value={adminNoteInput}
                    onChange={(e) => setAdminNoteInput(e.target.value)}
                    placeholder="Log conversation notes, preferred times, student diagnostic observations..."
                    className="w-full p-2.5 border border-slate-300 rounded-lg text-xs placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-600 shadow-2xs"
                  />
                  <div className="mt-1 flex justify-end">
                    <button
                      onClick={() =>
                        notesMutation.mutate({
                          id: selectedLead.id,
                          notes: adminNoteInput,
                        })
                      }
                      disabled={notesMutation.isPending}
                      className="px-2.5 py-1 text-xs font-semibold bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded shadow-2xs transition"
                    >
                      Save Note
                    </button>
                  </div>
                </div>

                {/* Assigned Tutor Status */}
                {selectedLead.demo_tutor && (
                  <div className="p-2.5 bg-purple-50/60 border border-purple-200 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UserCheck className="w-4 h-4 text-purple-700" />
                      <div>
                        <div className="font-bold text-purple-900">Assigned Demo Tutor</div>
                        <div className="text-slate-600">
                          {availableTutors.find((t) => t.id === selectedLead.demo_tutor)
                            ? `${availableTutors.find((t) => t.id === selectedLead.demo_tutor)?.first_name} ${availableTutors.find((t) => t.id === selectedLead.demo_tutor)?.last_name}`
                            : selectedLead.demo_tutor}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setIsScheduleModalOpen(true)}
                      className="text-xs font-bold text-purple-700 hover:underline"
                    >
                      Change
                    </button>
                  </div>
                )}

                {/* Dispatch & Booking Action Bar */}
                <div className="pt-2 space-y-2 border-t border-slate-100">
                  <button
                    onClick={() => {
                      setScheduleTutorId(selectedLead.demo_tutor || availableTutors[0]?.id || "");
                      setIsScheduleModalOpen(true);
                    }}
                    className="w-full py-2.5 px-4 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg shadow-2xs transition flex items-center justify-center gap-2"
                  >
                    <Calendar className="w-4 h-4" />
                    {selectedLead.status === "scheduled_demo"
                      ? "Reschedule Demo Slot"
                      : "Schedule Demo Session"}
                  </button>

                  <button
                    onClick={() => {
                      const params = new URLSearchParams({
                        studentName: selectedLead.student_name,
                        studentEmail: selectedLead.email,
                        parentName: selectedLead.parent_name,
                        grade: selectedLead.grade || "",
                      });
                      navigate(`${ROUTES.ADMIN_ENROLLMENTS_CREATE.path}?${params.toString()}`);
                    }}
                    className="w-full py-2 px-4 bg-white hover:bg-slate-50 text-purple-700 border border-purple-200 font-bold rounded-lg shadow-2xs transition flex items-center justify-center gap-2"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Convert to Manual Enrollment
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-12 text-center text-slate-400 text-xs">
              Select an inquiry from the list to view diagnostic dossier and dispatch actions.
            </div>
          )}
        </div>
      </div>

      {/* Schedule Demo Dialog */}
      <Dialog open={isScheduleModalOpen} onOpenChange={setIsScheduleModalOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-slate-900">
              Schedule Trial Demo Session
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Assign an accredited rfLearn tutor and set the diagnostic demo date and time for{" "}
              <strong className="text-slate-800">{selectedLead?.student_name}</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3.5 py-2 text-xs">
            <div>
              <label className="font-bold text-slate-700 block mb-1">Assign Tutor</label>
              <select
                value={scheduleTutorId}
                onChange={(e) => setScheduleTutorId(e.target.value)}
                className="w-full h-9 px-3 border border-slate-300 rounded-lg text-xs bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-600 shadow-2xs font-medium"
              >
                <option value="">-- Select Tutor --</option>
                {availableTutors.map((tutor) => (
                  <option key={tutor.id} value={tutor.id}>
                    {tutor.first_name} {tutor.last_name} ({tutor.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-bold text-slate-700 block mb-1">Demo Date</label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  className="w-full h-9 px-3 border border-slate-300 rounded-lg text-xs bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-600 shadow-2xs font-medium"
                />
              </div>
              <div>
                <label className="font-bold text-slate-700 block mb-1">Start Time</label>
                <input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="w-full h-9 px-3 border border-slate-300 rounded-lg text-xs bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-600 shadow-2xs font-medium"
                />
              </div>
            </div>

            <div className="p-3 bg-purple-50/60 rounded-lg border border-purple-200 text-xs text-purple-900 leading-relaxed">
              Confirming this slot will update the inquiry status to{" "}
              <strong>Demo Scheduled</strong>, record the assigned educator, and prepare the demo meeting bridge.
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <button
              onClick={() => setIsScheduleModalOpen(false)}
              className="px-3.5 py-1.5 text-xs font-semibold bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (selectedLead) {
                  scheduleDemoMutation.mutate({
                    id: selectedLead.id,
                    tutorId: scheduleTutorId,
                    date: scheduleDate,
                    time: scheduleTime,
                  });
                }
              }}
              disabled={scheduleDemoMutation.isPending}
              className="px-4 py-1.5 text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white rounded-lg shadow-2xs transition disabled:opacity-50"
            >
              {scheduleDemoMutation.isPending ? "Confirming..." : "Confirm Demo Slot"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DemoBookingsPage;
